<template>
    <search-result-list-item ref="listItemRef" class="trip-pattern-list-item">
        <template #default="{ active, expanded }">
            <div class="trip-pattern-list-item__header d-flex flex-nowrap">
                <div class="trip-leg-summary__container">
                    <trip-leg-summary
                        :model-value="modelValue.legs"
                        :active-summary="active"
                        :variant="expanded ? 'expanded' : 'auto'"
                        :hide-leg-duration="hideLegDuration"
                        :hide-stop-time="expanded"
                        no-wrap
                    />
                </div>
                <v-spacer />
                <div class="py-2">
                    <total-duration v-if="modelValue.duration" :model-value="modelValue.duration" />
                </div>
            </div>

            <notification-messages-list
                v-if="!expanded && bookingNotificationMessages?.length"
                :messages="bookingNotificationMessages"
                :compact="!expanded"
                disabled
                class="py-2 px-3"
            />
            <div v-if="isDebugMode" class="debug-info px-3 py-1">{{ modelValue.debugInfo }}</div>
        </template>

        <template #append="{ expanded }">
            <div class="d-flex justify-end pa-3">
                <expand-toggle-button
                    data-id="tripPatternListItemExpandToggleBtn"
                    :filled="onlineBookingIsAvailable"
                    :active="expanded"
                    :label="toggleBtnLabel"
                    @click.stop="$emit('click:expand')"
                />
            </div>
        </template>

        <template v-if="!isMobile" #details>
            <template v-if="showBookingCard">
                <booking-combo-card ref="bookingComboCardRef" color="transparent" class="py-4" />
                <v-divider />
            </template>
            <div class="py-4">
                <trip-leg-list v-if="modelValue.legs">
                    <trip-leg-list-item
                        v-for="(leg, i) in modelValue.legs"
                        :key="leg.id"
                        ref="tripLegItems"
                        :model-value="leg"
                        :first="i === 0"
                        :last="i === modelValue.legs.length - 1"
                        :previous-leg="modelValue.legs[i - 1]"
                        :next-leg="modelValue.legs[i + 1]"
                    />
                </trip-leg-list>
                <slot name="post-trip-leg-list" />
            </div>
        </template>
    </search-result-list-item>
</template>

<script setup lang="ts">
import { NotificationMessageType } from '@/types/webapi';
import { SearchResultListItem, NotificationMessagesList, ExpandToggleButton } from '@/features/common/components';
import { TripLegSummary, TripLegList, TripLegListItem } from '..';
import TotalDuration from './TotalDuration.vue';
import { useDisplay } from 'vuetify';
import { BookingComboCard } from '@/features/bookings/components';
import { OnlineBookingIsAvailableKey } from '@/injectionKeys';
import { ref, inject, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { TripPattern } from '@/features/trips/models';

interface Props extends /* @vue-ignore */ NonNullable<InstanceType<typeof SearchResultListItem>['$props']> {
    /** The trip pattern data transfer object containing legs and duration information */
    modelValue: TripPattern;

    /** Flag indicating if the trip pattern is in non-transit mode */
    isInNonTransitMode?: boolean;

    /** Flag to enable debug mode which shows additional debug information */
    isDebugMode?: boolean;

    /** Whether to hide the leg duration */
    hideLegDuration?: boolean;
}

interface Emits {
    (e: 'click:expand'): void;
}

const props = defineProps<Props>();
defineEmits<Emits>();
const listItemRef = ref<InstanceType<typeof SearchResultListItem>>();
const bookingComboCardRef = ref<InstanceType<typeof BookingComboCard>>();
const { t } = useI18n();
const { mobile: isMobile } = useDisplay();
const onlineBookingIsAvailable = inject(OnlineBookingIsAvailableKey);
const showBookingCard = computed(() => !!(onlineBookingIsAvailable?.value && listItemRef.value?.expanded));
const bookingNotificationMessages = computed(() =>
    props.modelValue.legs
        ?.flatMap(leg => leg.notificationMessages)
        .filter(
            message =>
                message.type === NotificationMessageType.OnlineBookingArrangement ||
                message.type === NotificationMessageType.BookingConfirmation
        )
);

const toggleBtnLabel = (expanded: boolean) =>
    expanded
        ? undefined
        : onlineBookingIsAvailable?.value
          ? t('common.resources.searchResult.detailsAndBookingBtnLabel', 'Details and booking')
          : t('trips.resources.tripSearchResult.showDetailsBtnLabel', 'Show trip details');

watch(
    () => listItemRef.value?.active,
    value => {
        if (!value) bookingComboCardRef?.value?.reset?.();
    }
);
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.trip-pattern-list-item {
    --v-color-search-list-result-list-item-bg: #f5fcf8;

    .debug-info {
        font-size: 10px;
    }

    &__header {
        padding: 4px 12px 0;

        .search-result-list-item--active &,
        .search-result-list-item--focused & {
            border-bottom: 1px solid var(--v-color-green);
            background-color: #ddefe6;

            .trip-leg-summary {
                --trip-leg-summary-line-color: var(--v-color-neutral-600);
            }
        }
    }
}

.trip-leg-summary__container {
    width: calc(100% - 37px); // 25px is the width of the duration component + 12px padding
}
</style>
